import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { useDispatch } from 'react-redux';

import { checkIsInOrder } from '@app/features/apply-react-signing/libs/checkIsInOrder';
import { toSigningStateForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { getEmbeddedDraft } from '@app/features/integration/embeddedDraft/lib/getEmbeddedDraft';
import useTemplateDocumentFileUploadByUrl from '@app/hooks/documentSetup/useTemplateDocumentFileUploadByUrl';
import { usePDF } from '@app/hooks/usePDF';
import { modalOpen } from '@app/store/modal/actions';
import { templateCreateLoadPdfSuccess } from '@app/store/template/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';

export function useSetupTemplateByEmbeddedDraft() {
  const dispatch = useDispatch();
  const { initialize } = useSigningState();
  const { setPDF, resetPDF } = usePDF();

  const { mutateAsync: uploadTemplateFileByUrl } = useTemplateDocumentFileUploadByUrl({
    onError: (error) => {
      dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
    },
    onSuccess: ({ fileId, pdfArrayBuffer, title, url }) => {
      dispatch(
        templateCreateLoadPdfSuccess({
          fileId,
          pdfArrayBuffer,
          title: title ?? '',
          url,
        })
      );
    },
  });

  async function load({ draftId }: { draftId: string }) {
    resetPDF();
    // draft 불러오기
    const draft = await getEmbeddedDraft(draftId);

    const signingState = toSigningStateForSetup(
      draft.attachments,
      draft.participants,
      draft.fields,
      // TODO: @Juyeong-Byeon getNewDraftById api 에서 스펙과 다르게 null로 주고 있음. 이부분 해결 필요,
      draft.fieldGroups ?? [],
      draft.verifications
    );

    if (draft.file?.url != null) {
      // 임베디드의 경우 pdf 없이 템플릿 에디터로 접근후 pdf를 업로드 할 수 있다
      await uploadTemplateFileByUrl({ title: draft.title ?? undefined, url: draft.file.url });
      const { data: pdf } = await getFileFromURL(draft.file.url);
      setPDF(pdf);
    }

    // 초기화
    initialize({
      attachments: signingState.attachments,
      fields: signingState.fields,
      verifications: signingState.verifications,
      participants: signingState.participants,
      document: {
        title: draft.title ?? '',
        pdfPassword: draft.pdfPassword,
        pdfFile: {
          id: draft.file?.fileId ?? '',
          url: draft.file?.url ?? '',
        },
      },
      ui: {
        isInOrder: checkIsInOrder(signingState.participants),
        insertingField: null,
        lastInsertedField: createInitialLastInsertedField(),
      },
    });
  }

  return {
    load,
  };
}
